import { BrandDetailProductsWrapper } from './BrandDetailProductsWrapper';
import MetaRobots from 'components/Basic/Head/MetaRobots';
import Heading from 'components/Basic/Heading/Heading';
import Overlay from 'components/Basic/Overlay/Overlay';
import CategorySubcategories from 'components/Blocks/Categories/CategorySubcategories/CategorySubcategories';
import { FilterPanel } from 'components/Blocks/Product/Filter/FilterPanel/FilterPanel';
import { SelectedFilters } from 'components/Blocks/Product/Filter/SelectedParameters/SelectedFilters';
import SortingBar from 'components/Blocks/SortingBar/SortingBar';
import Webline from 'components/Layout/Webline/Webline';
import {
    categoryDetailContentTwClass,
    categoryDetailPanelIconTwClass,
    categoryDetailPanelOpenerTwClass,
    categoryDetailSortingPanelTwClass,
    categoryDetailSortingWrapperTwClass,
    getCategoryPanelWrapperTwClass,
} from 'components/Pages/CategoryDetail/CategoryDetail';
import { CategoryHeader } from 'components/Pages/CategoryDetail/CategoryHeader';
import { SORT_QUERY_PARAMETER_NAME } from 'helpers/queryParams/queryParamNames';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useSeoTitleWithPagination } from 'helpers/seo/useSeoTitleWithPagination';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useRouter } from 'next/router';
import { FilterIcon } from 'public/svg/IconsSvg';
import React, { useCallback, useRef, useState } from 'react';
import { BrandDetailType } from 'types/brand';

type BrandDetailProps = {
    brand: BrandDetailType;
};

const BrandDetail: FC<BrandDetailProps> = ({ brand }) => {
    const t = useTypedTranslationFunction();
    const currentPage = useCurrentPageQuery();

    const router = useRouter();
    const isSorted = SORT_QUERY_PARAMETER_NAME in router.query;

    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const paginationScrollTargetRef = useRef<HTMLDivElement>(null);
    const title = useSeoTitleWithPagination(brand.productConnection.totalCount, brand.name, brand.seoH1);

    const handlePanelOpenerClick = useCallback(() => {
        const body = document.getElementsByTagName('body')[0];

        setIsPanelOpen((prev) => {
            body.style.overflow = prev ? 'visible' : 'hidden';

            return !prev;
        });
    }, []);

    return (
        <>
            {isSorted && <MetaRobots content="noindex, follow" />}
            <Webline>
                <CategoryHeader
                    title={title}
                    description={brand.description}
                    slug={brand.slug}
                    uuid={brand.uuid}
                    imageSrc={brand.image}
                    name={brand.name}
                    currentPage={currentPage}
                />
            </Webline>
            <Webline>
                <Heading type="h3">{t('You can find products from the line in these categories')}</Heading>
                <CategorySubcategories
                    listedItems={brand.categories}
                    filter={{
                        brands: [brand.uuid],
                    }}
                    displayProductCount={false}
                />
            </Webline>
            <Webline>
                <div className="relative mb-8 flex flex-col vl:mb-10 vl:flex-row">
                    {brand.productConnection.productFilterOptions !== null && (
                        <>
                            <div className={getCategoryPanelWrapperTwClass(isPanelOpen)}>
                                <FilterPanel
                                    panelCloseHandler={handlePanelOpenerClick}
                                    totalCount={brand.productConnection.totalCount}
                                    productFilterOptions={brand.productConnection.productFilterOptions}
                                />
                            </div>
                            {isPanelOpen && <Overlay isHiddenOnDesktop onClick={handlePanelOpenerClick} />}
                            <div className={categoryDetailContentTwClass} ref={paginationScrollTargetRef}>
                                <div className={categoryDetailSortingWrapperTwClass}>
                                    <SelectedFilters filterOptions={brand.productConnection.productFilterOptions} />
                                    <div className={categoryDetailSortingPanelTwClass}>
                                        <div
                                            className={categoryDetailPanelOpenerTwClass}
                                            id="js-category-detail-panel"
                                            onClick={handlePanelOpenerClick}
                                        >
                                            <FilterIcon className={categoryDetailPanelIconTwClass} />
                                            {t('Product filter')}
                                        </div>
                                        <SortingBar
                                            sorting={brand.productConnection.orderingMode}
                                            totalCount={brand.productConnection.totalCount}
                                        />
                                    </div>
                                </div>
                                <BrandDetailProductsWrapper
                                    brand={brand}
                                    paginationScrollTargetRef={paginationScrollTargetRef}
                                />
                            </div>
                        </>
                    )}
                </div>
            </Webline>
        </>
    );
};

export default BrandDetail;
